/* guide U6 / U2-v: `.pick-list.add-cart` 행 (담기 전·완료) */

import { ctaTranslate } from "../../lib/ctaTranslate";
import { postToHost } from "../../lib/postmessage";
import { ProductNameText } from "../../lib/ProductNameText";
import { productDisplayName } from "../../lib/productText";
import { Button } from "../../ui";
import {
  fallbackProductImage,
  handleProductImageError,
  normalizeProductImage,
} from "../../lib/fallback-image";
import { useLabel } from "../../lib/labels";
import {
  PickBadges,
  PickProductBody,
  type PickBadgeEntry,
  type PickFlagEntry,
  type PickPriceBlock,
} from "./pickShared";
import { saleTagsToFlags } from "./saleTagNeo";
import { cartSkuForProduct } from "./cartSku";

/* 되묻기 심플 카드 CTA 분기(제품상세 Feature/Spec §3).
 * cart 재확인·Eligible(§5)은 variant 미지정 → Add to Basket(toolCall) 유지.
 * Feature/Spec 되묻기는 발화만 전송(toolCall 없음) — plan_act 가 SKU 해소·capability 라우팅.
 * sibling(G-6)은 Feature Summary + Add to Basket dual CTA. */
export type AddCartCtaVariant = "feature" | "spec" | "select" | "sibling";

// ctaKey/fallback 은 user 클릭 발화(ctaTranslate)용. 표시 라벨(label)은 display —
// useLabel(labels 블록)로 분리 해석한다(i18n SOT: docs/domain/i18n).
type SpeechCtaVariant = Exclude<AddCartCtaVariant, "select" | "sibling">;

const _CTA_BY_VARIANT: Record<
  SpeechCtaVariant,
  { labelKey: string; labelEn: string; ctaKey: string; fallback: (sku: string) => string }
> = {
  feature: {
    labelKey: "aiFeatureSummary",
    labelEn: "AI Feature Summary",
    ctaKey: "cart_confirm.feature_summary_cta",
    fallback: (sku) => `${sku} features summary`,
  },
  spec: {
    labelKey: "aiSpecsSummary",
    labelEn: "AI Specs Summary",
    ctaKey: "cart_confirm.spec_summary_cta",
    fallback: (sku) => `${sku} spec summary`,
  },
};

const _SELECT_CTA = { labelKey: "select", labelEn: "Select" } as const;

const _SIBLING_FEATURE = {
  labelKey: "aiFeatureSummary",
  labelEn: "Feature Summary",
  ctaKey: "cart_confirm.feature_summary_cta",
  fallback: (sku: string) => `${sku} features summary`,
} as const;

export interface AddCartProductFields {
  sku: string;
  feedSku?: string;
  rawSku?: string;
  modelKey?: string;
  modelCode?: string;
  name?: string;
  image?: string | null;
  price?: PickPriceBlock | number;
  badges?: PickBadgeEntry[];
  flags?: PickFlagEntry[];
  pdpUrl?: string;
  // GR-7: 시크릿 쿠폰 eligible 헤드 제품 강조 (`.pick-item.highlight`)
  highlighted?: boolean;
  // 장바구니 연관상품. 말풍선은 Add {sku} to Basket. packageId 가 있으면 패키지 담기.
  addBySku?: boolean;
  packageId?: string;
  anchorSku?: string;
  // AISASSIT-108: 담기완료 행 수량 표기("Qty N"). 미전달 시 1(담기 최소 단위).
  quantity?: number;
  // 케어플랜 행은 이미지와 이름만 보인다.
  kind?: "care_plan";
  siblingValue?: string;
}

interface AddCartPickItemProps {
  item: AddCartProductFields;
  sessionToken: string;
  /* 되묻기 capability(제품상세 Feature/Spec §3). 미지정 시 Add to Basket(장바구니/Eligible). */
  ctaVariant?: AddCartCtaVariant;
  /* 멀티 심플 carousel(`.pick-list--carousel.is-multiple`) 여부. true 면 프로모션을
   * 상단 배지가 아니라 사진 밑 텍스트 eyebrow 로 노출한다(SimpleProductCard 가 주입). */
  carousel?: boolean;
}

/** 심플 카드 행 — Add to Basket(기본) 또는 Feature/Spec 되묻기 CTA(§3) */
export function AddCartPickItem({
  item,
  sessionToken,
  ctaVariant,
  carousel,
}: AddCartPickItemProps) {
  const displayName = productDisplayName(item.name, item.sku);
  // 프로모션 표시: 출처(sale_tags→badges)는 동일하고, **레이아웃**에 따라 형태만 분기한다.
  //  - 멀티 carousel(is-multiple): 사진 밑 텍스트 eyebrow(`flags`/`.product-tag-flag`) — Figma.
  //  - 단건 카드(장바구니 재확인 1건 등): 기존 상단 배지(`badges`/`.pick-tag`) 유지.
  const explicitFlags = item.flags || [];
  const flags: PickFlagEntry[] = carousel
    ? explicitFlags.length > 0
      ? explicitFlags
      : saleTagsToFlags(item.badges || [])
    : explicitFlags;
  const badges = carousel ? [] : item.badges || [];
  // GR-7: eligible 헤드 제품 강조 — 추천/주문 카드와 동일한 `.pick-item.highlight`.
  const itemClass = item.highlighted ? "pick-item highlight" : "pick-item";

  const variantCta =
    ctaVariant && ctaVariant !== "select" && ctaVariant !== "sibling"
      ? _CTA_BY_VARIANT[ctaVariant]
      : null;
  const addToBasketLabel = useLabel("addToBasket", "Add to Basket");
  const featureSummaryLabel = useLabel(
    _SIBLING_FEATURE.labelKey,
    _SIBLING_FEATURE.labelEn,
  );
  const variantLabel = useLabel(
    ctaVariant === "select"
      ? _SELECT_CTA.labelKey
      : variantCta?.labelKey ?? "addToBasket",
    ctaVariant === "select"
      ? _SELECT_CTA.labelEn
      : variantCta?.labelEn ?? "Add to Basket",
  );

  const handleAddToBasket = () => {
    const cartSku = cartSkuForProduct(item);
    const relatedSku = item.feedSku?.trim() || cartSku;
    const packageId = item.packageId?.trim();
    const anchorSku = item.anchorSku?.trim();
    if (item.addBySku) {
      const args: Record<string, string> = { sku: relatedSku };
      if (packageId && anchorSku) {
        args.packageId = packageId;
        args.anchorSku = anchorSku;
        args.relatedSku = relatedSku;
      }
      postToHost(
        "chat.send_prompt",
        { message: `Add ${item.sku} to Basket` },
        {
          sessionId: sessionToken,
          toolCall: { tool: "commerce_add_to_cart", args },
        },
      );
      return;
    }
    // asklg_shop_add_to_cart_click 는 백엔드가 담기 성공 시 발화한다(말로 담기 = 카드 버튼).
    void ctaTranslate({
      code: "recommend.add_1x_to_my_cart",
      params: { name: displayName, sku: item.sku },
      sessionToken,
      fallback: `Add ${displayName} to my basket`,
      toolCall: {
        tool: "commerce_add_to_cart",
        args: { sku: cartSku },
      },
    });
  };

  const handleSummaryCta = () => {
    if (ctaVariant === "select") {
      // BRFrame은 message가 비어 있으면 chat.send_prompt를 버린다.
      void ctaTranslate({
        code: "purchase_clarify.select_cta",
        params: { sku: item.sku },
        sessionToken,
        fallback: `Select ${item.sku}`,
        toolCall: {
          tool: "commerce_purchase_clarify_select",
          args: { sku: item.sku },
        },
      });
      return;
    }
    const cta =
      ctaVariant === "sibling" ? _SIBLING_FEATURE : variantCta!;
    // Feature/Spec/Sibling Feature Summary — 발화만 전송. plan_act PI-1b 가 SKU·capability 라우팅.
    void ctaTranslate({
      code: cta.ctaKey,
      params: { sku: item.sku },
      sessionToken,
      fallback: cta.fallback(item.sku),
    });
  };

  // select 는 _CTA_BY_VARIANT 에 없음 — ctaVariant 존재만으로 clarify CTA 분기.
  const ctaLabel = ctaVariant ? variantLabel : addToBasketLabel;
  const onCtaClick = ctaVariant ? handleSummaryCta : handleAddToBasket;

  return (
    <li className={itemClass}>
      <div className="pick-item--inner">
        <PickBadges badges={badges} />

        <PickProductBody
          sku={item.sku}
          name={item.name}
          image={item.image}
          price={item.price}
          flags={flags}
        />

        <div className={ctaVariant === "sibling" ? "pick-btns pick-btns--sibling" : "pick-btns"}>
          {ctaVariant === "sibling" ? (
            <>
              <Button variant="secondary" size="xs" fullWidth onClick={handleSummaryCta}>
                {featureSummaryLabel}
              </Button>
              <Button variant="primary" size="xs" fullWidth onClick={handleAddToBasket}>
                {addToBasketLabel}
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="xs"
              onClick={() => onCtaClick()}
            >
              {ctaLabel}
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

interface AddCartDoneItemProps {
  item: AddCartProductFields;
}

/** 장바구니 담기 완료 — 단일 제품 행(기획 §3 / Figma 3105-17080).
 *  카드 chrome 과 Go to Basket 버튼은 상위(CartAddedCard)가 1개만 그린다.
 *  다건(§3-2)도 동일 행을 세로로 나열한다(캐러셀 아님). */
export function AddCartDoneItem({ item }: AddCartDoneItemProps) {
  const displayName = productDisplayName(item.name, item.sku);
  const qtyLabel = useLabel("qty", "Qty");
  const isCarePlan = item.kind === "care_plan";

  return (
    <li className="pick-item cart-added__item">
      <div className="pick-item--inner">
        <div className="pick-product">
          <div className="product-img">
            <img
              src={
                item.image
                  ? normalizeProductImage(item.image)
                  : fallbackProductImage()
              }
              alt={displayName}
              loading="lazy"
              draggable={false}
              onError={handleProductImageError}
            />
          </div>
          <div className="product-inner">
            {!isCarePlan && (
              <div className="product-sku">
                <span title={`SKU: ${item.sku}`}>{item.sku}</span>
              </div>
            )}
            <div className="product-ufn">
              <ProductNameText name={item.name} sku={item.sku} />
            </div>
            {/* AISASSIT-108: 수량 표기(퍼블 .product-quantity, "Qty N"). 미전달 시 1. */}
            {!isCarePlan && (
              <div className="product-quantity">
                <span>
                  {qtyLabel} {item.quantity ?? 1}
                </span>
              </div>
            )}
            {/* AISASSIT-108(재오픈): 담기완료 행에 가격 미노출(기획). */}
          </div>
        </div>
      </div>
    </li>
  );
}
